import { Alert, Box, Button, LinearProgress, Stack, type Theme, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { ApplicationTypes } from 'sunlife-domain'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { addVariantToCart } from '../../../shopify/shopify-ajax-client'
import { configuratorInterface } from '../../../systems/configurator-interface'

interface FinalizeCartSuccessDialogContentProps {
  finalizationUuid: string
  onClose?: () => void
}

export const FinalizeCartSuccessDialogContent = ({
  finalizationUuid,
  onClose,
}: FinalizeCartSuccessDialogContentProps) => {
  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()

  const { configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const logger = useLogger()

  const cartUrl = useMemo(() => {
    if (!window?.Shopify) {
      logger.warn('Shopify is not loaded')
      return null
    }
    return window.Shopify.routes.root + 'cart'
  }, [logger])

  const handleBack = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Initial)])
    }
  }, [onClose, navigationSystem, storeSystem])

  const getResult = useCallback(async () => {
    const data = await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid, {
      pollingTimeout: 20 * 60 * 1000,
    })
    if (!data.shopifyProduct?.productVariantId) {
      logger.error('PRODUCT VARIANT ID NOT FOUND', { data })
      throw new Error('PRODUCT VARIANT ID NOT FOUND')
    }
    const cartResult = await addVariantToCart(data.shopifyProduct.productVariantId.toString())
    return cartResult
  }, [apiClient, configurationId, finalizationUuid, logger])

  const { data, isError, isLoading } = useQuery({
    queryKey: ['finalize', finalizationUuid],
    queryFn: getResult,
  })

  const handleViewCart = useCallback(() => {
    if (cartUrl) {
      window.open(cartUrl, '_self')
    }
  }, [cartUrl])

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      header={
        <Box
          component="div"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.background.default,
            aspectRatio: '2.9',
            display: 'grid',
            placeItems: 'center',
            color: 'green',
          })}
        >
          <PhxIcon name="CheckCircle" style={{ fontSize: 200 }} />
        </Box>
      }
      body={
        <>
          <Stack direction="column" spacing={1} sx={{ pl: 0.5 }}>
            {isError && (
              <Alert severity="error">
                Couldn't add to cart. Please try again later or contact support if the issue persists.
              </Alert>
            )}
            {isLoading && (
              <>
                <Typography>Creating new Cart Item...</Typography>
                <LinearProgress />
              </>
            )}
            {data && (
              <>
                <Typography variant="h4" gutterBottom id="cart-success-dialog-title">
                  Added to Cart!
                </Typography>
                <Typography variant="body1" gutterBottom id="cart-success-dialog-description">
                  Your custom swimming shorts configuration has been successfully added to your cart.
                </Typography>
              </>
            )}
          </Stack>
        </>
      }
      controls={
        <>
          <Stack direction="row" spacing={2}>
            <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
              Back to Design
            </Button>
            {data && (
              <Button variant="contained" onClick={handleViewCart}>
                View Cart
              </Button>
            )}
          </Stack>
        </>
      }
    />
  )
}
